import React from 'react'
import { Card, CardContent, AsyncContentProgress, FloatingActionButton } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { useCommunityEnabled } from '@/modules/comunidade/hooks/useCommunityEnabled'
import { useVisionEnabled } from '../hooks/useVisionEnabled'
import { useVisionPostEnabled } from '../hooks/useVisionPostEnabled'
import { useVisionList } from '../hooks/useVisionList'
import { createVision } from '../services/visionService'
import { VisionHeader } from '../components/VisionHeader'
import { NewVisionModal, type NewVisionModalSubmitParams } from '../components/NewVisionModal'
import { VisionList } from '../components/VisionList'
import { useLocation } from 'react-router-dom'
import { uploadAndAddVisionPostAttachments } from '../services/visionPostAttachmentsUploadService'
import { useVisionModeratorEnabled } from '../hooks/useVisionModeratorEnabled'
import { useVisionHeaderWarningText } from '../hooks/useVisionHeaderWarningText'
import { Pencil } from 'lucide-react'
import { useFreemiumPlanStatus } from '@/modules/plans/hooks/useFreemiumPlanStatus'

export default function VisoesPage() {
  const enabled = useVisionEnabled()
  const communityEnabled = useCommunityEnabled()
  const postEnabled = useVisionPostEnabled()
  const visionModeratorEnabled = useVisionModeratorEnabled(enabled === true)
  const headerWarningText = useVisionHeaderWarningText()
  const { profile, user } = useAuth()
  const { isFreemium } = useFreemiumPlanStatus()
  const { add: toast } = useToast()
  const [createOpen, setCreateOpen] = React.useState(false)
  const location = useLocation()

  const currentUserId = user?.id ? String(user.id) : null
  const isMaster = profile?.role === 'master'
  const canCreatePosts = postEnabled === true
  const canDeleteOthers = isMaster || visionModeratorEnabled === true

  const { items, loading, loadingMore, hasMore, error, reload, loadMore } =
    useVisionList(25)

  const focusParams = React.useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || '')
      const focusRaw = params.get('focus')
      const msgRaw = params.get('community_message_id')
      const focusId = focusRaw ? Number(String(focusRaw).trim()) : null
      const messageId = msgRaw ? Number(String(msgRaw).trim()) : null
      return {
        focusVisionId:
          Number.isFinite(focusId) && (focusId as number) > 0
            ? Math.floor(focusId as number)
            : null,
        focusMessageId:
          Number.isFinite(messageId) && (messageId as number) > 0
            ? Math.floor(messageId as number)
            : null,
      }
    } catch {
      return { focusVisionId: null, focusMessageId: null }
    }
  }, [location.search])

  React.useEffect(() => {
    const focusVisionId = focusParams.focusVisionId
    const focusMessageId = focusParams.focusMessageId
    if (!focusVisionId || !focusMessageId) return

    const exists = items.some((it) => Number(it.id) === focusVisionId)
    if (exists) return
    if (!hasMore) return
    if (loading || loadingMore) return

    loadMore()
  }, [
    focusParams.focusVisionId,
    focusParams.focusMessageId,
    hasMore,
    items,
    loadMore,
    loading,
    loadingMore,
  ])

  if (enabled === null) {
    return (
      <div className="mx-auto mt-8 w-full max-w-3xl">
        <Card>
          <CardContent className="py-10 text-center text-sm text-foreground/70">
            Carregando acesso ao modulo de Visoes...
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!enabled) {
    return (
      <div className="mx-auto mt-8 w-full max-w-3xl">
        <Card>
          <CardContent className="py-10 text-center text-sm text-foreground/70">
            Seu plano ou papel atual nao possui acesso ao modulo de Visoes.
          </CardContent>
        </Card>
      </div>
    )
  }

  async function handleCreateVision(params: NewVisionModalSubmitParams) {
    try {
      const created = await createVision({
        title: params.title,
        html: params.html,
        linkedOpportunityId: params.linkedOpportunityId,
        linkedTimelineRootId: params.linkedTimelineRootId,
      })
      if (created) {
        if (params.newAttachments.length > 0) {
          const res = await uploadAndAddVisionPostAttachments(
            created.id,
            params.newAttachments,
          )
          if (res.failedCount > 0) {
            toast({
              title: 'Visao criada, mas alguns anexos falharam',
              description: `Anexos enviados: ${res.uploadedCount}. Falhas: ${res.failedCount}.`,
              variant: 'warning',
              soft: true,
            })
          }
        }
        toast({
          title: 'Visao criada com sucesso',
          description: 'Sua visao foi publicada para os membros autorizados.',
          variant: 'success',
          soft: true,
        })
        reload()
      }
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error('Erro ao criar visao', e)
      toast({
        title: 'Falha ao criar visao',
        description: e?.message || 'Ocorreu um erro ao salvar a visao. Tente novamente.',
        variant: 'error',
      })
    }
  }

  return (
    <div className="mx-auto mt-8 w-full max-w-5xl">
      <VisionHeader warningText={headerWarningText} />

      {canCreatePosts && (
        <NewVisionModal
          open={createOpen}
          onOpenChange={setCreateOpen}
          onSubmit={handleCreateVision}
        />
      )}

      {error && (
        <Card className="mt-4 border border-red-500/40 bg-red-500/5">
          <CardContent className="text-sm text-red-400">{error}</CardContent>
        </Card>
      )}

      <AsyncContentProgress
        loading={loading}
        label="Carregando visoes do mercado..."
        className="mt-4"
      >
        <VisionList
          items={items}
          communityEnabled={communityEnabled === true}
          currentUserId={currentUserId}
          canDeleteOthers={canDeleteOthers}
          focusVisionId={focusParams.focusVisionId}
          focusMessageId={focusParams.focusMessageId}
          showFreemiumCard={isFreemium}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
          onReload={reload}
        />
      </AsyncContentProgress>

      {canCreatePosts && (
        <FloatingActionButton
          label="Adicionar visao"
          onClick={() => setCreateOpen(true)}
          className="bg-lime-500 text-black hover:bg-lime-400 focus-visible:ring-lime-500"
        >
          <Pencil className="h-6 w-6" />
        </FloatingActionButton>
      )}
    </div>
  )
}
